import type { ProviderId } from "./providerRegistry";
import { providerFromProjectPath } from "./projectIdentity";

const AUTH_RETURN_TO_KEY = "auth_return_to";
const AUTH_SOURCE_KEY = "auth_source";
export { providerFromProjectPath };

/**
 * Campaign attribution has to cross an identity boundary. Marketing surfaces
 * run cookieless with `person_profiles: "never"`, so a landing-page visitor and
 * the signed-in user they become are different PostHog identities that cannot
 * be stitched. `sign_in_started` carries `?source=` but the conversion events
 * fire later, on the app surface, after an OAuth round-trip through the
 * provider — by then the query string is gone.
 *
 * Parking the source alongside the existing return-to (same sessionStorage,
 * same auth flow, cleared on read) lets `sign_in_completed` and
 * `provider_connected` report which channel produced an ACTIVATED team, not
 * just a click. Deliberately narrow: a short campaign label from our own
 * links, never a user identifier and never free-form text from the page.
 */
const SAFE_SOURCE = /^[a-z0-9_-]{1,32}$/i;

/**
 * One rule for every campaign label, wherever it enters. Bounds the shape so a
 * crafted `?source=` can't become free-form text on an analytics property, and
 * lowercases so `Reddit` and `reddit` don't split a channel's numbers in two.
 * Returns null for anything unusable, letting callers fall back.
 */
export function normalizeAuthSource(value: string | null | undefined): string | null {
  if (!value || !SAFE_SOURCE.test(value)) return null;
  return value.toLowerCase();
}

export function saveAuthSource(source: string | null | undefined): void {
  const normalized = normalizeAuthSource(source);
  if (!normalized) return;
  sessionStorage.setItem(AUTH_SOURCE_KEY, normalized);
}

export function consumeAuthSource(): string | null {
  const value = sessionStorage.getItem(AUTH_SOURCE_KEY);
  sessionStorage.removeItem(AUTH_SOURCE_KEY);
  return normalizeAuthSource(value);
}

function isSafeReturnTo(value: string): boolean {
  return (
    value.startsWith("/") &&
    !value.startsWith("//") &&
    !value.startsWith("/auth/") &&
    !value.startsWith("/oauth/")
  );
}

export function requiredProviderFromSearch(search: string): ProviderId | null {
  const params = new URLSearchParams(search);
  const explicit = params.get("provider");
  if (explicit === "github" || explicit === "gitlab") return explicit;
  return providerFromProjectPath(params.get("project"));
}

export function returnToFromSearch(search: string): string | null {
  const value = new URLSearchParams(search).get("returnTo");
  if (!value || !isSafeReturnTo(value)) return null;
  return value;
}

export function currentAuthReturnTo(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function saveAuthReturnTo(returnTo: string): void {
  if (!returnTo || !isSafeReturnTo(returnTo)) return;
  sessionStorage.setItem(AUTH_RETURN_TO_KEY, returnTo);
}

export function readAuthReturnTo(): string | null {
  const value = sessionStorage.getItem(AUTH_RETURN_TO_KEY);
  return value && isSafeReturnTo(value) ? value : null;
}

export function consumeAuthReturnTo(fallback = "/app"): string {
  const value = readAuthReturnTo() ?? fallback;
  sessionStorage.removeItem(AUTH_RETURN_TO_KEY);
  return value;
}

export function withOAuthComplete(returnTo: string): string {
  const url = new URL(returnTo, window.location.origin);
  url.searchParams.set("oauth", "complete");
  return `${url.pathname}${url.search}${url.hash}`;
}

export function buildLoginPath(returnTo: string): string {
  const requiredProvider = requiredProviderFromSearch(
    new URL(returnTo, window.location.origin).search
  );
  const login = new URL("/login", window.location.origin);
  if (requiredProvider && requiredProvider !== "fixtures") {
    login.searchParams.set("provider", requiredProvider);
  }
  return `${login.pathname}${login.search}`;
}

export function buildSessionExpiredLoginPath(returnTo: string): string {
  const loginPath = buildLoginPath(returnTo);
  const login = new URL(loginPath, window.location.origin);
  login.searchParams.set("reason", "session_expired");
  return `${login.pathname}${login.search}`;
}
